import React from 'react';
import { Container, Row, Col, Fade, Card, CardBody } from 'reactstrap';
import { Form } from 'react-final-form';
import produce from 'immer';
import moment from 'moment';
import { QuestionnaireManager } from '../../../modules/unsw-fhir-questionnaire';
import { PatientMiniCard } from '../../Cards';
import SectionJumplist from '../../../modules/unsw-fhir-questionnaire/Questionnaire/SectionJumplist';
import { QuestionnaireContext } from '../../../modules/unsw-fhir-questionnaire/Contexts';
import { createAnswerMap } from '../../../modules/unsw-fhir/QuestionnaireResponse/helpers';
import ProgressBar from '../../../modules/unsw-fhir-questionnaire/Questionnaire/ProgressBar';
import View from '../../../modules/unsw-fhir-questionnaire/Questionnaire/View';
import QuestionnaireFormNavigation from '../../../modules/unsw-fhir-questionnaire/Questionnaire/Navigation';
import { LoadingCard } from '../../../components/Cards';
import {
  IQuestionnaireResponse,
  QuestionnaireResponseStatus,
} from '../../../modules/unsw-fhir/QuestionnaireResponse';
import { IQuestionnaire } from '../../../modules/unsw-fhir/Questionnaire';
import { QuestionnaireItem } from '../../QuestionnaireItems';
import {
  putQuestionnaireResponse,
  postQuestionnaireResponse,
} from '../../../apis/questionnaireResponse';
import { SupportingLinkModal } from '../../Modals/SupportingLink';
import { usePatient } from '../../../contexts/patient';

const QuestionnaireLoadingCard = (props: {
  state: 'requesting' | 'prefilling' | 'done';
}) => {
  const states = [
    {
      id: '0',
      display: 'Downloading Questionnaire',
      done: props.state === 'prefilling' || props.state === 'done',
    },
    {
      id: '1',
      display: 'Pre-filling Questionnaire',
      done: props.state === 'done',
    },
  ];
  return <LoadingCard items={states} />;
};

const assignMetadata = (
  questionnaireResponse: IQuestionnaireResponse | undefined,
  patientId: string,
  questionnaireId: string,
  author: string,
  status: QuestionnaireResponseStatus,
) =>
  questionnaireResponse
    ? produce(questionnaireResponse, draft => {
        draft.author = { reference: `Practitioner/${author}` };
        draft.questionnaire = {
          reference: `Questionnaire/${questionnaireId}`,
        };
        draft.subject = { reference: `Patient/${patientId}` };
        draft.authored = moment().toISOString();
        draft.status = status;
        return draft;
      })
    : undefined;

const submitQuestionnaire = async (
  questionnaireResponse: IQuestionnaireResponse | undefined,
  patientId?: string,
  practitionerId?: string,
  questionnaire?: IQuestionnaire,
  status: QuestionnaireResponseStatus = 'in-progress',
) => {
  if (!questionnaireResponse) return undefined;

  if (!patientId || !questionnaire || !questionnaire.id || !practitionerId)
    throw new Error('Missing metadata!');
  const mQR = assignMetadata(
    questionnaireResponse,
    patientId,
    questionnaire.id,
    practitionerId,
    status,
  );

  if (mQR && mQR.id) {
    return await putQuestionnaireResponse(mQR.id, mQR);
  } else {
    return await postQuestionnaireResponse(mQR);
  }
};

export type QuestionnaireProps = {
  disabled?: boolean;
  questionnaire?: IQuestionnaire;
  questionnaireResponse?: IQuestionnaireResponse;
  onComplete?: (
    questionnaire: IQuestionnaire,
    questionnaireResponse?: IQuestionnaireResponse,
  ) => void | Promise<void>;
  authorId: string;
  patientId?: string;
  loading?: boolean;
  setQuestionnaireResponse: (
    questionnaireResponse: IQuestionnaireResponse | undefined,
  ) => void;
  prefill?: (
    response: IQuestionnaireResponse | undefined,
  ) => Promise<IQuestionnaireResponse> | IQuestionnaireResponse;
  beforeSave?: (
    response: IQuestionnaireResponse | undefined,
    questionnaire: IQuestionnaire,
  ) =>
    | Promise<IQuestionnaireResponse | undefined>
    | IQuestionnaireResponse
    | undefined;
};

const Questionnaire = ({
  questionnaire,
  questionnaireResponse,
  setQuestionnaireResponse,
  onComplete,
  beforeSave,
  authorId,
  loading,
}: QuestionnaireProps) => {
  const { patient } = usePatient();
  const patientId = patient ? patient.id : undefined;
  if (!questionnaire || !patientId) return null;
  return (
    <QuestionnaireManager
      questionnaire={questionnaire}
      response={questionnaireResponse}
      onComplete={onComplete ? onComplete : () => {}}
      onSave={async (value, qtnnaire) => {
        const mQuestionnareResponse = beforeSave
          ? await beforeSave(value, qtnnaire)
          : value;

        const response = await submitQuestionnaire(
          mQuestionnareResponse,
          patientId,
          authorId,
          questionnaire,
        );
        setQuestionnaireResponse(response);
      }}>
      <Container fluid className="mt-3">
        <Row>
          <Col className="sticky-top d-none d-sm-block questionnaire-sidebar">
            <Row noGutters>
              <Col>
                <PatientMiniCard />
              </Col>
            </Row>
            <Row noGutters className="mt-3">
              <Col>
                <SectionJumplist />
              </Col>
            </Row>
          </Col>
          <Col sm={8} sx={12} className="mb-3">
            {loading ? (
              <Fade>
                <QuestionnaireLoadingCard state="prefilling" />
              </Fade>
            ) : (
              <Fade>
                <Card>
                  <CardBody>
                    <QuestionnaireContext.Consumer>
                      {({ response, onSubmit }) => {
                        return (
                          <Form
                            onSubmit={onSubmit}
                            keepDirtyOnReinitialize={true}
                            initialValues={
                              response ? createAnswerMap(response) : undefined
                            }>
                            {({ handleSubmit }) => (
                              <>
                                <Row noGutters>
                                  <Col className="mb-2">
                                    <ProgressBar />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    <View
                                      handleSubmit={handleSubmit}
                                      Component={QuestionnaireItem}
                                    />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col
                                    className="d-flex justify-content-between"
                                    size={12}>
                                    <QuestionnaireFormNavigation />
                                  </Col>
                                </Row>
                              </>
                            )}
                          </Form>
                        );
                      }}
                    </QuestionnaireContext.Consumer>
                  </CardBody>
                </Card>
              </Fade>
            )}
          </Col>
        </Row>
        <SupportingLinkModal />
      </Container>
    </QuestionnaireManager>
  );
};

export default Questionnaire;
